import { useEffect, useRef, useState } from 'react';
import type { OrderType } from '@alphabet-hunter/shared';
import type { Mode, Screen } from './types';
import { useGame } from './context/GameContext';
import { useToast } from './context/ToastContext';
import { useSinglePlayer } from './hooks/useSinglePlayer';
import { useTheme } from './hooks/useTheme';
import { parsePendingJoinCode } from './lib/gameCode';
import { shareGameCode } from './lib/share';
import { LandingScreen } from './components/LandingScreen';
import { SetupScreen } from './components/SetupScreen';
import { LobbyScreen } from './components/LobbyScreen';
import { GameScreen } from './components/GameScreen';
import { ResultsScreen } from './components/ResultsScreen';
import { Toast } from './components/Toast';

export default function App() {
  const { showToast } = useToast();
  const mp = useGame();
  const sp = useSinglePlayer();
  const { isDark, toggle } = useTheme();

  const [screen, setScreen] = useState<Screen>('landing');
  const [landingMode, setLandingMode] = useState<'idle' | 'join'>('idle');
  const [playerName, setPlayerName] = useState('');
  const [mode, setMode] = useState<Mode>(null);
  const [orderType, setOrderType] = useState<OrderType>('sequential');
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(null);
  const [localJoinError, setLocalJoinError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const code = parsePendingJoinCode();
    if (code) {
      setPendingJoinCode(code);
      setLandingMode('join');
    }
  }, []);

  // Screen transitions follow server/local game state, mirroring each
  // message handler's showScreen() call in the pre-migration client.
  useEffect(() => {
    if (mode !== 'multiplayer' && mode !== 'join') return;
    if (!mp.game) return;
    if (mp.game.status === 'lobby') setScreen('lobby');
    else if (mp.game.status === 'playing') setScreen('game');
    else if (mp.game.status === 'finished') setScreen('results');
  }, [mp.game, mode]);

  useEffect(() => {
    if (mode !== 'single') return;
    if (!sp.game) return;
    setScreen(sp.game.status === 'finished' ? 'results' : 'game');
  }, [sp.game, mode]);

  useEffect(() => {
    if (mp.joinError) {
      setScreen('landing');
      setLandingMode('join');
    }
  }, [mp.joinError]);

  function requireName(): boolean {
    const val = playerName.trim();
    if (!val) {
      nameInputRef.current?.focus();
      showToast('Please enter your name first.');
      return false;
    }
    setPlayerName(val);
    return true;
  }

  function handleSelectSingle() {
    if (!requireName()) return;
    setMode('single');
    setScreen('setup');
  }

  function handleSelectMultiplayer() {
    if (!requireName()) return;
    setMode('multiplayer');
    setScreen('setup');
  }

  function handleShowJoin() {
    setMode('join');
    setLandingMode('join');
  }

  function handleBackFromJoin() {
    setLandingMode('idle');
    setLocalJoinError(null);
    mp.clearJoinError();
  }

  function handleDemo() {
    const name = playerName.trim() || 'Demo Player';
    setPlayerName(name);
    setMode('single');
    setOrderType('sequential');
    sp.start('sequential', name);
    showToast('Demo mode — type any word that starts with the shown letter!', 4000);
  }

  // Enter in the name field while a join code is already known just moves
  // focus along — the join-code input (when shown) handles its own Enter.
  function handleNameEnter() {
    if (pendingJoinCode) setLandingMode('join');
  }

  function handleStartSetup() {
    if (mode === 'single') {
      sp.start(orderType, playerName);
    } else if (mode === 'multiplayer') {
      mp.createGame(playerName, orderType);
    }
  }

  function handleJoinSubmit(code: string) {
    if (!requireName()) return;
    if (code.length < 6) {
      setLocalJoinError('Please enter the full 6-character code.');
      return;
    }
    setLocalJoinError(null);
    mp.joinGame(playerName, code);
  }

  function handleWordSubmit(word: string) {
    if (!word.trim()) return;
    if (mode === 'single') {
      sp.submitWord(word);
    } else {
      mp.submitWord(word);
    }
  }

  function handleLeaveGame() {
    if (mode === 'multiplayer' || mode === 'join') mp.leaveGame();
    sp.reset();
    setMode(null);
    setLandingMode('idle');
    setScreen('landing');
  }

  function handlePlayAgain() {
    if (mode === 'single') {
      sp.start(orderType, playerName);
    } else {
      setScreen('setup');
    }
  }

  const activeGame = mode === 'single' ? sp.game : mp.game;
  const activeFeedback = mode === 'single' ? sp.feedback : mp.wordFeedback;

  switch (screen) {
    case 'landing':
      return (
        <>
          <LandingScreen
            playerName={playerName}
            onPlayerNameChange={setPlayerName}
            nameInputRef={nameInputRef}
            onNameEnter={handleNameEnter}
            pendingJoinCode={pendingJoinCode}
            isDark={isDark}
            onToggleTheme={toggle}
            landingMode={landingMode}
            onShowJoin={handleShowJoin}
            onBackFromJoin={handleBackFromJoin}
            joinError={mp.joinError ?? localJoinError}
            onClearJoinError={() => {
              setLocalJoinError(null);
              mp.clearJoinError();
            }}
            onSelectSingle={handleSelectSingle}
            onSelectMultiplayer={handleSelectMultiplayer}
            onSubmitJoin={handleJoinSubmit}
            onDemo={handleDemo}
          />
          <Toast />
        </>
      );
    case 'setup':
      return (
        <>
          <SetupScreen
            orderType={orderType}
            onOrderTypeChange={setOrderType}
            onStart={handleStartSetup}
            onBack={() => setScreen('landing')}
          />
          <Toast />
        </>
      );
    case 'lobby':
      return (
        <>
          <LobbyScreen
            game={mp.game}
            gameCode={mp.gameCode}
            isHost={mp.isHost}
            onShare={() => {
              if (mp.gameCode) void shareGameCode(mp.gameCode, showToast);
            }}
            onStart={mp.startGame}
            onLeave={() => {
              mp.leaveGame();
              setScreen('landing');
            }}
          />
          <Toast />
        </>
      );
    case 'game':
      return (
        <>
          <GameScreen game={activeGame} feedback={activeFeedback} onSubmitWord={handleWordSubmit} onLeave={handleLeaveGame} />
          <Toast />
        </>
      );
    case 'results':
      return (
        <>
          <ResultsScreen game={activeGame} onPlayAgain={handlePlayAgain} onNewGame={handleLeaveGame} />
          <Toast />
        </>
      );
  }
}
